import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import useMunicipalityUsers from 'api/hooks/use-employee-options'
import useRoles from 'api/hooks/use-roles'
import { usePermissions } from 'api/hooks/use-permissions'
import Select from 'react-select'

// The param key each subject type writes to on the audit-log filter state.
const PARAM_KEYS = {
  user: 'userId',
  role: 'roleId',
  permission: 'permissionId'
}

function TaggedSubject({ params, setParams }) {
  const [selected, setSelected] = useState('')

  const { isSuccess: usersLoaded, data: users } = useMunicipalityUsers({})
  const { isSuccess: rolesLoaded, data: roles } = useRoles({
    params: { as_options: true, orderBy: 'name', orderDirection: 'asc' }
  })
  const { isSuccess: permissionsLoaded, data: permissions } = usePermissions({
    params: { page: 1, perPage: 1000 }
  })

  // One grouped option list spanning all three subject types. Each option
  // carries its `type` and a composite `value` so react-select can match it
  // back to whichever of userId/roleId/permissionId is currently set.
  const groupedOptions = useMemo(() => {
    const groups = []

    if (usersLoaded) {
      groups.push({
        label: 'Users',
        options: users.map((user) => ({
          label: user.full_name,
          value: `user:${user.id}`,
          type: 'user',
          id: user.id
        }))
      })
    }

    if (rolesLoaded) {
      groups.push({
        label: 'Roles',
        options: roles.map((role) => ({
          label: role.name,
          value: `role:${role.id}`,
          type: 'role',
          id: role.id
        }))
      })
    }

    if (permissionsLoaded) {
      const permissionOptions = permissions.data
        .map((permission) => ({
          label: permission.description,
          value: `permission:${permission.id}`,
          type: 'permission',
          id: permission.id
        }))
        .sort((a, b) => (a?.label ? a.label.localeCompare(b.label) : Infinity))
      groups.push({ label: 'Permissions', options: permissionOptions })
    }

    return groups
  }, [usersLoaded, users, rolesLoaded, roles, permissionsLoaded, permissions])

  // Derive the active selection from whichever subject param is set.
  useEffect(() => {
    const active = Object.entries(PARAM_KEYS).find(
      ([, key]) => params[key]
    )
    if (!active) {
      setSelected('')
      return
    }
    const [type, key] = active
    const group = groupedOptions.find((g) =>
      g.options.some((option) => option.type === type)
    )
    const match = group?.options.find((option) => option.id === params[key])
    setSelected(match || '')
  }, [
    params.userId,
    params.roleId,
    params.permissionId,
    groupedOptions
  ]) // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (val) => {
    setSelected(val || '')
    // Clear every subject param, then set the one for the chosen type so only
    // a single subject is ever filtered at once.
    const cleared = { userId: '', roleId: '', permissionId: '' }
    if (val) {
      cleared[PARAM_KEYS[val.type]] = val.id
    }
    setParams({ ...params, ...cleared })
  }

  return (
    <div style={{ minWidth: '220px' }}>
      <Select
        options={groupedOptions}
        value={selected}
        isClearable={true}
        placeholder="Search User,Role,Permission"
        onChange={onChange}
        id="tagged-subject-selector"
      />
    </div>
  )
}

TaggedSubject.propTypes = {
  params: PropTypes.object.isRequired,
  setParams: PropTypes.func.isRequired
}

export default TaggedSubject
